import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiCreditCard, FiKey, FiShield } from "react-icons/fi";
import { getApiMessage } from "../../config/taskApi";
import { parseAadhaarSendOtpResponse, parseAadhaarVerifyResponse } from "../../utils/aadhaarVerifyUtils";
import { checkCustomerByPan } from "../../utils/panCustomerCheck";
import {
  getAadhaarNumberSecure,
  getAadhaarReferenceSecure,
  sanitizeAadhaarProfile,
  setAadhaarNumberSecure,
  setAadhaarReferenceSecure,
} from "../../utils/applyFlowStorage";
import {
  APPLY_LOAN_AADHAAR_OTP_RESEND_SECONDS,
  clearApplyLoanAadhaarOtpDraft,
  getApplyLoanAadhaarNumber,
  getApplyLoanAadhaarOtpDraft,
  persistApplyLoanAadhaarNumber,
  restoreApplyLoanSecureFields,
  saveApplyLoanAadhaarOtpDraft,
  saveApplyLoanStep,
} from "../../utils/applyLoanStorage";
import { flushSaveEnquiry } from "../../utils/saveEnquiry";
import "./AadhaarInput.css";

const PAN_STATUS_VERIFIED = "PAN Verification Complete!";
const AADHAAR_STATUS_VERIFIED = "Aadhaar Verification Complete!";
const AADHAAR_OTP_FALLBACK_SECONDS = APPLY_LOAN_AADHAAR_OTP_RESEND_SECONDS;

const isAadhaarOtpSuccessMessage = (message) => {
  const normalized = String(message || "").toLowerCase();
  return (
    normalized.includes("sent") ||
    normalized.includes("success") ||
    normalized.includes("generated")
  );
};

const isPanNotFoundOnline = (responseCode, responseMessage) =>
  responseCode === "ENI004" ||
  String(responseMessage || "").toLowerCase().includes("no information found");

const readStoredAadhaarVerified = () => {
  try {
    restoreApplyLoanSecureFields();
    const flagged = localStorage.getItem("aadhaar_verified") === "true";
    const raw = localStorage.getItem("aadhaarData");
    if (!raw) return { verified: false, aadhaar: "" };
    const profile = JSON.parse(raw);
    if (!profile || typeof profile !== "object") {
      return { verified: false, aadhaar: "" };
    }
    const hasProfile = Boolean(
      profile.name ||
        profile.fullName ||
        profile.district ||
        profile.dist ||
        profile.state
    );
    if (!hasProfile) return { verified: false, aadhaar: "" };

    let num = getApplyLoanAadhaarNumber();
    if (num.length !== 12) {
      num = String(
        profile.aadhaar_number ||
          profile.aadhaarNumber ||
          profile.uid ||
          ""
      )
        .replace(/\D/g, "")
        .slice(0, 12);
    }
    if (num.length !== 12) {
      try {
        const loan = JSON.parse(localStorage.getItem("loanFormData") || "{}");
        num = String(loan.aadhaar_number || loan.aadhaar || "")
          .replace(/\D/g, "")
          .slice(0, 12);
      } catch {
        /* ignore */
      }
    }

    // Profile exists from successful verify — keep verified even if number briefly missing
    if (!flagged && hasProfile) {
      localStorage.setItem("aadhaar_verified", "true");
    }
    if (num.length === 12) {
      persistApplyLoanAadhaarNumber(num);
    }

    if (!flagged && !hasProfile) {
      return { verified: false, aadhaar: "" };
    }

    return {
      verified: flagged || hasProfile,
      aadhaar: num.length === 12 ? num : "",
    };
  } catch {
    return { verified: false, aadhaar: "" };
  }
};

const IconField = ({ icon: Icon, children, className = "" }) => (
  <div className={`loan-icon-field ${className}`.trim()}>
    <span className="loan-icon-field__icon" aria-hidden="true">
      <Icon />
    </span>
    {children}
  </div>
);

const mergeLoanFormPan = (pan, panDetails, panStatus) => {
  let loan = {};
  try {
    const raw = localStorage.getItem("loanFormData");
    if (raw) loan = JSON.parse(raw);
  } catch {
    loan = {};
  }
  localStorage.setItem(
    "loanFormData",
    JSON.stringify({
      ...loan,
      pan,
      panStatus: panStatus || PAN_STATUS_VERIFIED,
      pan_verified: true,
      ...panDetails,
    })
  );
};

/**
 * Apply-loan step 2 only: PAN above Aadhaar OTP.
 * Does not affect apply-now AadhaarOtpStep1.
 */
const readInitialAadhaarOtpDraft = () => {
  const draft = getApplyLoanAadhaarOtpDraft();
  if (!draft) {
    return {
      aadhaar: "",
      consent: false,
      otpSent: false,
      retryTimer: 0,
      aadhaarError: "",
    };
  }
  setAadhaarNumberSecure(draft.aadhaar);
  const apiMessage = draft.apiMessage || "";
  const otpWasSent = draft.otpSent === true;

  if (!otpWasSent) {
    clearApplyLoanAadhaarOtpDraft();
    setAadhaarReferenceSecure("");
    return {
      aadhaar: draft.aadhaar,
      consent: false,
      otpSent: false,
      retryTimer: 0,
      aadhaarError: apiMessage,
    };
  }

  setAadhaarReferenceSecure(draft.referenceId || "");
  return {
    aadhaar: draft.aadhaar,
    consent: true,
    otpSent: true,
    retryTimer: draft.retryTimer,
    aadhaarError: isAadhaarOtpSuccessMessage(apiMessage) ? apiMessage : "",
  };
};

const ApplyLoanIdentityStep1 = ({
  onNext,
  hideHero = false,
  sendAadhaarOtpUrl = "https://fundswalla.in/loan_backend/sendAadhaarOtp.php",
  verifyAadhaarOtpUrl = "https://fundswalla.in/loan_backend/verifyAadhaarOtp.php",
  enquirySource,
}) => {
  const restoredAadhaar = readStoredAadhaarVerified();
  const initialOtp = restoredAadhaar.verified
    ? {
        aadhaar: restoredAadhaar.aadhaar,
        consent: true,
        otpSent: false,
        retryTimer: 0,
        aadhaarError: "",
      }
    : readInitialAadhaarOtpDraft();
  const [pan, setPan] = useState("");
  const [panVerifying, setPanVerifying] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [panError, setPanError] = useState("");
  const [panStatus, setPanStatus] = useState("");
  const [panDetails, setPanDetails] = useState({});
  const panVerifyLockRef = useRef(false);
  const aadhaarSendLockRef = useRef(false);
  const aadhaarVerifyLockRef = useRef(false);

  const [aadhaar, setAadhaar] = useState(
    restoredAadhaar.verified
      ? restoredAadhaar.aadhaar || initialOtp.aadhaar
      : initialOtp.aadhaar
  );
  const [consent, setConsent] = useState(
    restoredAadhaar.verified ? true : initialOtp.consent
  );
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [justVerified, setJustVerified] = useState(false);
  const [aadhaarVerified, setAadhaarVerified] = useState(
    restoredAadhaar.verified
  );
  const [aadhaarStatus, setAadhaarStatus] = useState(
    restoredAadhaar.verified ? AADHAAR_STATUS_VERIFIED : ""
  );
  const [otpSent, setOtpSent] = useState(
    restoredAadhaar.verified ? false : initialOtp.otpSent
  );
  const [otpError, setOtpError] = useState("");
  const [aadhaarError, setAadhaarError] = useState(
    restoredAadhaar.verified ? "" : initialOtp.aadhaarError
  );
  const [retryTimer, setRetryTimer] = useState(
    restoredAadhaar.verified ? 0 : initialOtp.retryTimer
  );

  const isPanFilled = pan.length === 10;
  const isAadhaarValid = /^\d{12}$/.test(aadhaar);
  const isIdentityReady = isPanFilled && isAadhaarValid;
  const isFormValid = panVerified && isAadhaarValid && consent;
  const isOtpValid = /^\d{6}$/.test(otp);
  const showAadhaarOtpPanel =
    otpSent && (!aadhaarError || isAadhaarOtpSuccessMessage(aadhaarError));

  useEffect(() => {
    try {
      const raw = localStorage.getItem("loanFormData");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const savedPan = String(parsed.pan || parsed.pan_verifiedPan || "")
        .toUpperCase()
        .trim();
      if (
        savedPan.length === 10 &&
        (parsed.pan_verified === true ||
          parsed.pan_verified === "true" ||
          localStorage.getItem("pan_verified") === "true")
      ) {
        setPan(savedPan);
        setPanVerified(true);
        setPanStatus(parsed.panStatus || PAN_STATUS_VERIFIED);
        setPanDetails({
          pan_responseCode: parsed.pan_responseCode || "",
          pan_responseMessage: parsed.pan_responseMessage || "",
          pan_referenceId: parsed.pan_referenceId || "",
          pan_firstName: parsed.pan_firstName || "",
          pan_lastName: parsed.pan_lastName || "",
          pan_address: parsed.pan_address || "",
          pan_gender: parsed.pan_gender || "",
          pan_aadhaarLinked: parsed.pan_aadhaarLinked || "",
          pan_dob: parsed.pan_dob || "",
          pan_fullName: parsed.pan_fullName || "",
          pan_category: parsed.pan_category || "",
          pan_maskedAadhaarNumber: parsed.pan_maskedAadhaarNumber || "",
          pan_verifiedPan: parsed.pan_verifiedPan || savedPan,
        });
      }
    } catch {
      /* ignore */
    }

    // Re-sync Aadhaar verified from durable storage (back / remount)
    const restored = readStoredAadhaarVerified();
    if (restored.verified) {
      setAadhaarVerified(true);
      setAadhaarStatus(AADHAAR_STATUS_VERIFIED);
      if (restored.aadhaar) setAadhaar(restored.aadhaar);
      setConsent(true);
      setOtpSent(false);
      setOtp("");
      setAadhaarError("");
    }
  }, []);

  useEffect(() => {
    if (retryTimer <= 0) return undefined;
    const interval = setInterval(() => {
      setRetryTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [retryTimer]);

  useEffect(() => {
    if (!otpSent || !aadhaarError || isAadhaarOtpSuccessMessage(aadhaarError)) return;
    setOtpSent(false);
    setOtp("");
    setRetryTimer(0);
    clearApplyLoanAadhaarOtpDraft();
    setAadhaarReferenceSecure("");
  }, [otpSent, aadhaarError]);

  const parseRetrySeconds = (apiMessage) => {
    const match = String(apiMessage || "").match(/\d+/);
    if (match && match[0]) {
      const n = parseInt(match[0], 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return AADHAAR_OTP_FALLBACK_SECONDS;
  };

  const markAadhaarOtpSentLocally = ({
    aadhaarValue,
    referenceId,
    apiMessage,
  }) => {
    const retrySeconds = parseRetrySeconds(apiMessage);
    const sentAt = Date.now();
    saveApplyLoanAadhaarOtpDraft({
      aadhaar: aadhaarValue,
      referenceId: referenceId || "",
      sentAt,
      retrySeconds,
      apiMessage: apiMessage || "",
      responseCode: "SOS174",
    });
    setAadhaarNumberSecure(aadhaarValue);
    setAadhaarReferenceSecure(referenceId || "");
    setOtpSent(true);
    setOtp("");
    setConsent(true);
    setRetryTimer(retrySeconds);
    setAadhaarError(isAadhaarOtpSuccessMessage(apiMessage) ? apiMessage : "");
  };

  const failAadhaarSend = (message) => {
    clearApplyLoanAadhaarOtpDraft();
    setAadhaarReferenceSecure("");
    setOtpSent(false);
    setOtp("");
    setRetryTimer(0);
    setAadhaarError(message || "Something went wrong.");
  };

  const resetToAadhaarEntry = () => {
    clearApplyLoanAadhaarOtpDraft();
    setOtpSent(false);
    setOtp("");
    setAadhaar("");
    setConsent(false);
    setRetryTimer(0);
    setAadhaarError("");
    setOtpError("");
    setAadhaarVerified(false);
    setAadhaarStatus("");
    localStorage.setItem("aadhaar_verified", "false");
    localStorage.removeItem("aadhaarData");
    localStorage.removeItem("aadhaar_mobile");
  };

  const clearAadhaarVerifiedState = () => {
    setAadhaarVerified(false);
    setAadhaarStatus("");
    localStorage.setItem("aadhaar_verified", "false");
  };

  const handlePanVerify = async (val) => {
    if (!val || val.length !== 10) return;
    if (panVerifyLockRef.current) return;

    panVerifyLockRef.current = true;
    setPanVerifying(true);
    setPanError("");
    setPanStatus("");

    const verifyPanUrl = "https://fundswalla.in/loan_backend/verifyPan.php";

    try {
      const customerCheck = await checkCustomerByPan(val);

      if (customerCheck.blocked) {
        setPanDetails({});
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanStatus("");
        setPanError(
          customerCheck.message ||
            "This PAN is already registered. Cannot re-apply."
        );
        return;
      }

      const statusMessage =
        customerCheck.isFresh || !customerCheck.message
          ? PAN_STATUS_VERIFIED
          : customerCheck.message;

      const verifyRes = await axios.get(verifyPanUrl, {
        params: { pan: val },
        timeout: 30000,
      });
      const verifyData = verifyRes.data?.data || {};
      const panInfo = verifyData?.data || {};
      const responseCode = verifyData.responseCode || "";
      const responseMessage = verifyData.responseMessage || "";
      const aadhaarLinked =
        panInfo.aadhaarLinked === true ||
        panInfo.aadhaarLinked === "true" ||
        panInfo.aadhaarLinked === "Y"
          ? "true"
          : panInfo.aadhaarLinked === false ||
              panInfo.aadhaarLinked === "false" ||
              panInfo.aadhaarLinked === "N"
            ? "false"
            : "";

      const nextPanDetails = {
        pan_responseCode: responseCode,
        pan_responseMessage: responseMessage,
        pan_referenceId: verifyData.referenceId || "",
        pan_firstName: panInfo.firstName || "",
        pan_lastName: panInfo.lastName || "",
        pan_address: panInfo.address || "",
        pan_gender: panInfo.gender || "",
        pan_aadhaarLinked: aadhaarLinked,
        pan_dob: panInfo.dob || "",
        pan_fullName: panInfo.fullName || "",
        pan_category: panInfo.category || "",
        pan_maskedAadhaarNumber: panInfo.maskedAadhaarNumber || "",
        pan_verifiedPan: panInfo.pan || val,
      };

      if (responseCode === "SRC001") {
        setPanDetails(nextPanDetails);
        setPanStatus(statusMessage);
        setPanVerified(true);
        localStorage.setItem("pan_verified", "true");
        mergeLoanFormPan(val, nextPanDetails, statusMessage);
        flushSaveEnquiry({
          pan: val,
          pancard: val,
          Name: nextPanDetails.pan_fullName || "",
          first_name: nextPanDetails.pan_firstName || "",
          step: "pan_verified",
          ...(enquirySource ? { source: enquirySource } : {}),
        });
        return;
      }

      if (isPanNotFoundOnline(responseCode, responseMessage)) {
        setPanDetails({});
        setPanStatus("");
        setPanVerified(false);
        localStorage.setItem("pan_verified", "false");
        setPanError("PAN does not exist. Enter your correct PAN.");
        return;
      }

      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError("Please enter a valid PAN number.");
    } catch (err) {
      setPanDetails({});
      setPanVerified(false);
      localStorage.setItem("pan_verified", "false");
      setPanError("PAN verification failed. Please try again.");
      setPanStatus("");
    } finally {
      panVerifyLockRef.current = false;
      setPanVerifying(false);
    }
  };

  const sendOtp = async () => {
    // First send needs PAN + consent; Resend only needs valid Aadhaar
    if (!otpSent && !isFormValid) return;
    if (otpSent && !isAadhaarValid) return;
    if (aadhaarSendLockRef.current) return;
    aadhaarSendLockRef.current = true;

    setLoading(true);
    setAadhaarError("");
    setOtpError("");

    try {
      const API_URL = sendAadhaarOtpUrl;
      const res = await axios.post(
        API_URL,
        { aadhaar_number: aadhaar },
        { headers: { "Content-Type": "application/json" } }
      );

      const responseData = res.data || {};
      const parsedSend = parseAadhaarSendOtpResponse(responseData);
      const apiMessage =
        getApiMessage(responseData) ||
        parsedSend.responseMessage ||
        "Unable to send OTP. Please try again.";

      if (parsedSend.success === false) {
        failAadhaarSend(apiMessage);
        return;
      }

      if (
        parsedSend.responseMessage === "Aadhar does not exist" ||
        parsedSend.responseCode === "EAE168"
      ) {
        failAadhaarSend("Invalid Aadhaar number.");
        return;
      }

      if (!parsedSend.isSuccess) {
        failAadhaarSend(apiMessage);
        return;
      }

      markAadhaarOtpSentLocally({
        aadhaarValue: aadhaar,
        referenceId: parsedSend.referenceId || "",
        apiMessage,
      });
    } catch (err) {
      failAadhaarSend(
        getApiMessage(err?.response?.data) || "OTP failed. Please try again."
      );
    } finally {
      aadhaarSendLockRef.current = false;
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp) {
      setOtpError("Please enter the OTP sent to your mobile.");
      return;
    }
    if (!panVerified) {
      setOtpError("Please verify your PAN first.");
      return;
    }
    if (aadhaarVerifyLockRef.current) return;
    aadhaarVerifyLockRef.current = true;

    setOtpLoading(true);
    setOtpError("");

    try {
      const backendUrl = verifyAadhaarOtpUrl;
      const res = await axios.post(backendUrl, {
        aadhaar_number: getAadhaarNumberSecure(),
        otp,
        referenceId: getAadhaarReferenceSecure(),
      });

      const parsed = parseAadhaarVerifyResponse(res.data);
      const isSuccess =
        parsed.responseCode === "SRC001" &&
        parsed.profile &&
        Object.keys(parsed.profile).length > 0;

      const responseMessage = String(parsed.responseMessage || "").toLowerCase();
      const isFailure =
        parsed.responseCode === "EAE168" ||
        responseMessage.includes("invalid") ||
        responseMessage.includes("incorrect") ||
        responseMessage.includes("failed");

      if (isSuccess) {
        setJustVerified(true);
        setAadhaarVerified(true);
        setAadhaarStatus(AADHAAR_STATUS_VERIFIED);
        clearApplyLoanAadhaarOtpDraft();

        const cleanAadhaar = String(aadhaar || getAadhaarNumberSecure() || "")
          .replace(/\D/g, "")
          .slice(0, 12);
        persistApplyLoanAadhaarNumber(cleanAadhaar);
        setAadhaarNumberSecure(cleanAadhaar);

        const safeProfile = sanitizeAadhaarProfile({
          ...parsed.profile,
          aadhaar_number: cleanAadhaar,
          aadhaarNumber: cleanAadhaar,
        });
        localStorage.setItem("aadhaarData", JSON.stringify(safeProfile));
        localStorage.setItem("aadhaar_verified", "true");

        if (parsed.mobile) {
          localStorage.setItem("aadhaar_mobile", parsed.mobile);
        } else {
          localStorage.removeItem("aadhaar_mobile");
        }

        mergeLoanFormPan(pan, panDetails, panStatus || PAN_STATUS_VERIFIED);
        try {
          const loan = JSON.parse(localStorage.getItem("loanFormData") || "{}");
          const district =
            safeProfile.district ||
            safeProfile.dist ||
            loan.district ||
            "";
          localStorage.setItem(
            "loanFormData",
            JSON.stringify({
              ...loan,
              aadhaar_number: cleanAadhaar,
              aadhaar: cleanAadhaar,
              district,
              state: loan.state || safeProfile.state || "",
              city: loan.city || district,
              city_name: loan.city_name || district,
            })
          );
        } catch {
          /* ignore */
        }
        localStorage.setItem("pan_verified", "true");
        saveApplyLoanStep(3);

        flushSaveEnquiry({
          pan,
          pancard: pan,
          aadhaar: cleanAadhaar,
          aadhaar_number: cleanAadhaar,
          Name: safeProfile?.name || panDetails.pan_fullName || "",
          first_name:
            safeProfile?.name ||
            panDetails.pan_firstName ||
            panDetails.pan_fullName ||
            "",
          aadhaar_name: safeProfile?.name || "",
          aadhaar_dob:
            safeProfile?.dateOfBirth ||
            safeProfile?.dob ||
            safeProfile?.birthDate ||
            "",
          aadhaar_gender: safeProfile?.gender || "",
          step: "aadhaar_verified",
          ...(enquirySource ? { source: enquirySource } : {}),
        });

        setTimeout(() => {
          onNext();
        }, 500);
      } else if (isFailure) {
        setOtpError(
          res.data?.data?.responseMessage ||
            res.data?.message ||
            res.data?.error ||
            "Incorrect OTP. Please try again."
        );
      } else {
        setOtpError(
          res.data?.data?.responseMessage ||
            res.data?.message ||
            res.data?.error ||
            "OTP verification failed. Please try again."
        );
      }
    } catch (err) {
      setOtpError(
        "OTP verification failed. Please check your internet connection and try again."
      );
    } finally {
      aadhaarVerifyLockRef.current = false;
      setOtpLoading(false);
    }
  };

  if (justVerified) {
    return (
      <div className="apply-form-head" style={{ textAlign: "center", border: "none" }}>
        <h2>{"\u2705"} Verified!</h2>
        <p>Redirecting...</p>
      </div>
    );
  }

  return (
    <div className="apply-now1-focus apply-loan-mobile-focus">
      <div className="apply-now1-focus-card">
        {!hideHero && (
        <div className="apply-form-head">
          <div className="apply-now1-hero">
            <span className="apply-now1-hero__eyebrow">Start Application</span>
            <h2>Verify identity</h2>
            <p>Enter PAN and Aadhaar, then confirm the OTP below.</p>
          </div>
        </div>
        )}

        <>
          <div className="apply-field">
            <label>
              PAN Number <span className="req">*</span>
            </label>
            <IconField icon={FiCreditCard}>
              <input
                type="text"
                maxLength={10}
                value={pan}
                placeholder="ABCDE1234F"
                disabled={showAadhaarOtpPanel || panVerifying || aadhaarVerified}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
                  setPan(v);
                  setPanError("");
                  setPanStatus("");
                  setPanVerified(false);
                  localStorage.setItem("pan_verified", "false");
                  if (v.length === 10) handlePanVerify(v);
                }}
                className={`apply-flow-input ${
                  panVerified ? "valid" : ""
                } ${panError ? "error" : ""}`}
              />
            </IconField>
            {panVerifying && (
              <div className="apply-flow-success">Verifying PAN...</div>
            )}
            {panStatus && !panError && (
              <div className="apply-flow-success">{panStatus}</div>
            )}
            {panError && <div className="apply-flow-error">{panError}</div>}
          </div>

          <div className="apply-field">
            <label>
              Aadhaar Number <span className="req">*</span>
            </label>
            <IconField icon={FiShield}>
              <input
                maxLength={12}
                value={aadhaar}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) {
                    setAadhaar(e.target.value);
                    setAadhaarError("");
                    if (aadhaarVerified) clearAadhaarVerifiedState();
                  }
                }}
                placeholder="XXXX XXXX XXXX"
                disabled={showAadhaarOtpPanel || !isPanFilled || aadhaarVerified}
                className={`apply-flow-input ${
                  isAadhaarValid || aadhaarVerified ? "valid" : ""
                } ${
                  aadhaarError && !isAadhaarOtpSuccessMessage(aadhaarError)
                    ? "error"
                    : ""
                }`}
              />
            </IconField>
            {aadhaarStatus && !aadhaarError && (
              <div className="apply-flow-success">{aadhaarStatus}</div>
            )}
            {aadhaarError && (
              <div
                className={
                  isAadhaarOtpSuccessMessage(aadhaarError)
                    ? "apply-flow-success"
                    : "apply-flow-error"
                }
              >
                {aadhaarError}
              </div>
            )}
          </div>

          {aadhaarVerified ? (
            <div className="apply-flow-actions apply-flow-actions--inline">
              <button
                type="button"
                className="apply-flow-btn apply-flow-btn--secondary"
                onClick={resetToAadhaarEntry}
              >
                Change Aadhaar
              </button>
              <button
                type="button"
                className="apply-flow-btn apply-flow-btn--primary"
                onClick={() => {
                  // Already verified path — one flush here (parent no longer flushes)
                  flushSaveEnquiry({
                    step: "aadhaar_verified",
                    ...(enquirySource ? { source: enquirySource } : {}),
                  });
                  onNext?.();
                }}
              >
                Continue
              </button>
            </div>
          ) : (
            <>
              {isIdentityReady && (
              <>
              <div className="apply-flow-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={() => setConsent(!consent)}
                  disabled={showAadhaarOtpPanel}
                />
                <label>
                  I authorize <b>Aadhaar verification</b> and{" "}
                  <b>digital communications</b>.
                </label>
              </div>

              {!showAadhaarOtpPanel && (
                <div className="apply-flow-actions apply-flow-actions--inline">
                  <button
                    className="apply-flow-btn apply-flow-btn--primary"
                    disabled={!isFormValid || loading || panVerifying}
                    onClick={sendOtp}
                  >
                    {loading ? "Sending..." : "Get OTP"}
                  </button>
                </div>
              )}
              </>
              )}

              {showAadhaarOtpPanel && (
                <div className="apply-now1-otp-panel">
                  <div className="apply-field">
                    <label>
                      OTP <span className="req">*</span>
                    </label>
                    <IconField icon={FiKey}>
                      <input
                        type="text"
                        maxLength={6}
                        value={otp}
                        placeholder="6-digit OTP"
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            setOtp(e.target.value);
                            setOtpError("");
                          }
                        }}
                        className={`apply-flow-input ${isOtpValid ? "valid" : ""} ${
                          otpError ? "error" : ""
                        }`}
                      />
                    </IconField>
                    {otpError && <div className="apply-flow-error">{otpError}</div>}
                  </div>

                  <p className="apply-now1-entry__resend">
                    {retryTimer > 0 ? (
                      <span>Resend in {retryTimer}s</span>
                    ) : (
                      <button
                        type="button"
                        className="apply-now1-entry__link"
                        onClick={sendOtp}
                        disabled={loading}
                      >
                        {loading ? "Sending..." : "Resend OTP"}
                      </button>
                    )}
                  </p>

                  <div className="apply-flow-actions apply-flow-actions--inline">
                    <button
                      className="apply-flow-btn apply-flow-btn--secondary"
                      onClick={resetToAadhaarEntry}
                      disabled={otpLoading}
                    >
                      Back
                    </button>
                    <button
                      className="apply-flow-btn apply-flow-btn--primary"
                      disabled={!isOtpValid || otpLoading || !panVerified}
                      onClick={verifyOtp}
                    >
                      {otpLoading ? "Verifying..." : "Verify"}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default ApplyLoanIdentityStep1;
